import { NOTE_EXTENSION } from '../config.js';
import { basename, replaceBasename } from '../paths.js';
import { fitBytes, MAX_NAME_BYTES, utf8Length } from './bytes.js';
import { extensionAt } from './extension.js';
import { foldName, isNoteName } from './fold.js';

/**
 * A conflict copy's name and path (docs/ARCHITECTURE.md §7): beside the note it
 * came from, recognisably the same note, and never a name already taken. Its
 * contents are `sync/conflicts.ts`.
 */

/**
 * `2026-09-15T14-32`. ISO to the minute with the `:` replaced: colons are
 * illegal in a filename on Windows and rejected outright by several provider
 * APIs, and a conflict copy the provider refuses to store is a lost edit.
 *
 * UTC rather than local time, so two devices in different zones that conflict
 * on the same note produce the same name rather than two copies a few hours
 * apart that look unrelated.
 */
export const conflictStamp = (at: Date): string => at.toISOString().slice(0, 16).replace(':', '-');

/**
 * The name for the copy, given the names already in the folder.
 *
 * Deliberately not run through `slugify`: the point of the name is that it
 * sorts next to the note it came from and is recognisably the same note, and a
 * slug would lowercase it and strip the parentheses that say what it is. The
 * characters it adds — spaces, parentheses, hyphens — are accepted by every
 * provider in §5.
 *
 * Two conflicts on the same note inside one minute get `-2`, `-3`, and so on,
 * because the minute-resolution stamp is not enough on its own and the second
 * copy must never overwrite the first.
 */
const conflictName = (
	stem: string,
	extension: string,
	at: Date,
	taken: Iterable<string>
): string => {
	const suffix = ` (conflict ${conflictStamp(at)})`;
	// `foldName`, the same one `uniqueFilename` uses, because it is the same
	// question: a name that differs from a taken one only in case or in normal
	// form is not actually free, and a conflict copy that landed on an existing
	// name would overwrite the very edit this whole rule exists to keep.
	const used = new Set([...taken].map(foldName));

	const free = (n: number): string => {
		const tail = n === 1 ? `${suffix}${extension}` : `${suffix}-${n}${extension}`;
		// The stem gives way, from its end, so the whole name fits what a
		// filesystem allows one: a name the provider (or the disk its desktop
		// client syncs to) refuses is an op that fails until the queue behind it
		// stops. A slug leaves room for one suffix; a copy of a copy, or a long
		// name from another tool, does not. Nothing already in the name is
		// stripped to make room — a copy of a copy should say so.
		//
		// Fitted before it is looked up, not after: two long names that differ
		// only past the cut are one name once cut.
		const candidate = `${fitBytes(stem, MAX_NAME_BYTES - utf8Length(tail))}${tail}`;
		return used.has(foldName(candidate)) ? free(n + 1) : candidate;
	};
	return free(1);
};

export const conflictFilename = (
	filename: string,
	at: Date,
	taken: Iterable<string> = []
): string => {
	// Folded, so `Report.MD` from a Windows tool loses its extension here rather
	// than keeping it and taking a second one — the same question `deriveTitle`
	// and `store/naming.ts` ask, answered the same way.
	const stem = isNoteName(filename) ? filename.slice(0, -NOTE_EXTENSION.length) : filename;
	return conflictName(stem, NOTE_EXTENSION, at, taken);
};

/**
 * The same name for a file that is not a note, keeping its own extension.
 *
 * `conflictFilename` gives every copy `.md`, which is right for a note and
 * wrong for anything else: a `photo.png` renamed `photo.png (conflict …).md`
 * is not a picture any more, and to the engine it is a note, which it then
 * tries to read as text. So the stamp goes before the extension, as it does
 * for a note: `photo (conflict 2026-09-15T14-32).png`.
 *
 * Where the extension starts is `extensionAt`'s answer, bounded so the stem
 * is always what gives way to fit the name (`conflictName`). A name that
 * starts with its only dot (`.env`) is a hidden file, not an extension with no
 * stem, and gets the stamp at the end, as a folder does.
 */
export const conflictNameKeepingExtension = (
	filename: string,
	at: Date,
	taken: Iterable<string> = []
): string => {
	const dot = extensionAt(filename);
	return conflictName(filename.slice(0, dot), filename.slice(dot), at, taken);
};

/**
 * The same name for a folder, which carries no extension.
 *
 * A folder needs one when a remote move lands on a path another of ours is
 * still at: the store keeps one row per path, so without moving that one aside
 * first its row is overwritten and the two folders' notes are merged into one
 * notebook. Usually it is there for the length of a batch — the entry saying
 * where that folder really went moves it on — but it can be left standing if
 * that entry never comes, so it is named the way a note would be rather than
 * something the user would not recognise.
 */
export const conflictFolderName = (name: string, at: Date, taken: Iterable<string> = []): string =>
	conflictName(name, '', at, taken);

export const conflictFolderPath = (path: string, at: Date, taken: Iterable<string> = []): string =>
	replaceBasename(path, conflictFolderName(basename(path), at, taken));

/** Where the copy of `path` goes: beside it, in the same folder. */
export const conflictPath = (path: string, at: Date, taken: Iterable<string> = []): string =>
	replaceBasename(path, conflictFilename(basename(path), at, taken));

/** The same, for a file that is not a note: its own extension kept (#187). */
export const conflictFilePath = (path: string, at: Date, taken: Iterable<string> = []): string =>
	replaceBasename(path, conflictNameKeepingExtension(basename(path), at, taken));
